import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider, Heading } from "@nn-design-system/react-component-library";
import CustomInput from "../../../../../Components/CustomInputs/CustomInput";
import CountryAutocompleteField from "../../../../../Components/CustomInputs/CountryAutocompleteField";
import GridContainer from "../../../../../Components/ReportFormTemplate/GridContainer";
import WholeLineContainer from "../../../../../Components/ReportFormTemplate/WholeLineContainer";
import { StoreType } from "../../../../../redux/store/store";
import { updateSecondStepReporterFields } from "../../../../../redux/reducers/claimReducer";
import { handleShowErrorMessage } from "../../../../../redux/reducers/claimFieldErrorsReducer";
import IdentificationDataBlock from "../../../../../Components/IdentificationDataBlock/IdentificationDataBlock";
import GetBirthDateFromPersonalId from "../../../../../Utils/GetBirthDateFromPersonalId";

const ReporterSection: React.FC = () => {
   const dispatch = useDispatch();
   const { reporterRole, reporterModel } = useSelector(
      (state: StoreType) => state.claimState.reportModel
   );
   const claimFieldErrorsMessages = useSelector(
      (state: StoreType) => state.claimFieldErrorsState
   );
   const authStatus = useSelector(
      (state: StoreType) => state.authState.userAuthenticationStatus
   );
   const isDeathClaim = useSelector((state: StoreType) =>
      state.claimState.selectedTypeOfClaims.some((claim) => claim === "death")
   );

   if (authStatus !== "agent" || isDeathClaim || reporterRole !== 2) {
      return null;
   }

   const model = reporterModel as Record<string, string | boolean>;
   const updateField = (field: string, value: string | boolean) => {
      dispatch(updateSecondStepReporterFields({ field, value }));
      if (
         field === "reporterPersonalId" &&
         typeof value === "string" &&
         !model.reporterDateOfBirth
      ) {
         const dateOfBirth = GetBirthDateFromPersonalId(value);
         if (dateOfBirth) {
            dispatch(updateSecondStepReporterFields({
               field: "reporterDateOfBirth",
               value: dateOfBirth,
            }));
         }
      }
   };
   const personFields = [
      ["reporterFirstname", "Jméno"],
      ["reporterLastname", "Příjmení"],
      ["reporterPhoneNumber", "Telefon"],
      ["reporterEmail", "E-mail"],
      ["reporterPersonalId", "Rodné číslo"],
      ["reporterDateOfBirth", "Datum narození"],
      ["reporterPlaceOfBirth", "Místo narození"],
   ];
   const addressFields = [
      ["reporterAddress", "Ulice a č.p."],
      ["reporterPostCode", "PSČ"],
      ["reporterCity", "Obec"],
   ];

   return (
      <GridContainer>
         <WholeLineContainer>
            <>
               <Heading variant="Medium" hasMargin={false} mb=".5rem" mt="1rem">
                  Osobní údaje oznamovatele (příjemce plnění)
               </Heading>
               <Divider variant="Brand" />
            </>
         </WholeLineContainer>
         {personFields.map(([field, label]) => (
            <CustomInput
               key={field}
               labelText={label}
               value={model[field] as string}
               validationText={handleShowErrorMessage(
                  field.charAt(0).toUpperCase() + field.slice(1),
                  claimFieldErrorsMessages
               )}
               type={field.endsWith("DateOfBirth") ? "date" : undefined}
               onChange={(e) => updateField(field, e.target.value)}
            />
         ))}
         <CountryAutocompleteField
            labelText="Státní příslušnost"
            placeholderText="Uveďte státní příslušnost"
            value={model.reporterCitizenship as string}
            validationText={handleShowErrorMessage("ReporterCitizenship", claimFieldErrorsMessages)}
            dependencyValue={true}
            fieldNameForScroll="ReporterCitizenship"
            onChange={(value) => updateField("reporterCitizenship", value)}
            reserveSecondColumn={false}
         />
         {addressFields.map(([field, label]) => (
            <CustomInput
               key={field}
               labelText={label}
               value={model[field] as string}
               validationText={handleShowErrorMessage(
                  field.charAt(0).toUpperCase() + field.slice(1),
                  claimFieldErrorsMessages
               )}
               onChange={(e) => updateField(field, e.target.value)}
            />
         ))}
         <CountryAutocompleteField
            labelText="Země"
            placeholderText="Uveďte zemi"
            value={model.reporterCountry as string}
            validationText={handleShowErrorMessage("ReporterCountry", claimFieldErrorsMessages)}
            dependencyValue={true}
            fieldNameForScroll="ReporterCountry"
            onChange={(value) => updateField("reporterCountry", value)}
            reserveSecondColumn={false}
         />
         <IdentificationDataBlock
            checked={Boolean(model.reporterPerformAmlIdentification)}
            identityCardType={model.reporterIdentityCardType as string}
            identityCardNumber={model.reporterIdentityCardNumber as string}
            identityCardIssuedBy={model.reporterIdentityCardIssuedBy as string}
            identityCardValidUntil={model.reporterIdentityCardValidUntil as string}
            updateField={(field, value) =>
               updateField(
                  field === "checked"
                     ? "reporterPerformAmlIdentification"
                     : `reporter${field.charAt(0).toUpperCase()}${field.slice(1)}`,
                  value
               )
            }
            validationKeys={{
               identityCardType: "ReporterIdentityCardType",
               identityCardNumber: "ReporterIdentityCardNumber",
               identityCardIssuedBy: "ReporterIdentityCardIssuedBy",
               identityCardValidUntil: "ReporterIdentityCardValidUntil",
            }}
            helperText="Pokud očekávaná výše pojistného plnění přesahuje 25 000 Kč, vyplňte prosím část „Doplnit identifikační údaje“ podle platného průkazu totožnosti oznamovatele."
            fieldErrors={claimFieldErrorsMessages}
         />
      </GridContainer>
   );
};

export default ReporterSection;
